/**
 * useCameraAnimation Hook
 * 
 * Camera keyframe animation system:
 * - Capture camera keyframes (position, rotation, target, FOV)
 * - Interpolate camera path (linear/smooth/cubic)
 * - Keyframe management (add, update, delete)
 * - Export/import JSON
 * - Timeline integration
 * 
 * @param {Object} cameraRef - Camera reference
 * @param {Object} controlsRef - OrbitControls reference
 * @returns {Object} Camera animation state dan methods
 */

import { useState, useRef, useCallback, useEffect } from 'react'
import { interpolateCameraAtTime } from '@utils/cameraInterpolation'

export function useCameraAnimation(cameraRef, controlsRef) {
  const [cameraKeyframes, setCameraKeyframes] = useState([])
  const [selectedCameraKeyframe, setSelectedCameraKeyframe] = useState(null)
  const [isRecordingCameraAnimation, setIsRecordingCameraAnimation] = useState(false)
  const [cameraInterpolationType, setCameraInterpolationType] = useState('smooth')
  const [cameraAnimationDuration, setCameraAnimationDuration] = useState(10)
  const [flashKeyframe, setFlashKeyframe] = useState(null)
  const [cameraAnimToast, setCameraAnimToast] = useState(null)

  const cameraKeyframesRef = useRef([])

  // Keep ref in sync
  useEffect(() => {
    cameraKeyframesRef.current = cameraKeyframes
  }, [cameraKeyframes])

  // Show toast notification
  const showCameraAnimToast = useCallback((message, type = 'success') => {
    setCameraAnimToast({ message, type })
    setTimeout(() => {
      setCameraAnimToast(null)
    }, 1500)
  }, [])

  // Add keyframe
  const addCameraKeyframe = useCallback((timeOrProgress) => {
    // Use window globals (more reliable than passing refs)
    const camera = window.MAIN_CAMERA || cameraRef?.current
    const controls = window.ORBIT_CONTROLS || controlsRef?.current
    
    if (!camera || !controls) {
      console.error('❌ addCameraKeyframe: camera or controls not initialized yet')
      return null
    }

    const newKeyframe = {
      id: Date.now(),
      time: timeOrProgress || cameraKeyframes.length * 2,
      position: {
        x: camera.position.x,
        y: camera.position.y,
        z: camera.position.z,
      },
      quaternion: {
        x: camera.quaternion.x,
        y: camera.quaternion.y,
        z: camera.quaternion.z,
        w: camera.quaternion.w,
      },
      target: {
        x: controls.target.x,
        y: controls.target.y,
        z: controls.target.z,
      },
      fov: camera.fov,
    }

    const updatedKeyframes = [...cameraKeyframes, newKeyframe].sort(
      (a, b) => a.time - b.time
    )

    setCameraKeyframes(updatedKeyframes)
    cameraKeyframesRef.current = updatedKeyframes
    setSelectedCameraKeyframe(newKeyframe.id)

    // Flash animation
    setFlashKeyframe(newKeyframe.id)
    setTimeout(() => {
      setFlashKeyframe(null)
    }, 600)

    showCameraAnimToast(
      `Keyframe #${updatedKeyframes.length} captured`,
      'success'
    )

    console.log('Keyframe captured:', newKeyframe)
    return newKeyframe
  }, [cameraKeyframes, showCameraAnimToast, cameraRef, controlsRef])

  // Update keyframe
  const updateCameraKeyframe = useCallback((keyframeId) => {
    // Use window globals (more reliable than passing refs)
    const camera = window.MAIN_CAMERA || cameraRef?.current
    const controls = window.ORBIT_CONTROLS || controlsRef?.current
    
    if (!camera || !controls) {
      console.error('❌ updateCameraKeyframe: camera or controls not initialized yet')
      return
    }

    const updated = cameraKeyframes.map((kf) =>
      kf.id === keyframeId
        ? {
            ...kf,
            position: {
              x: camera.position.x,
              y: camera.position.y,
              z: camera.position.z,
            },
            quaternion: {
              x: camera.quaternion.x,
              y: camera.quaternion.y,
              z: camera.quaternion.z,
              w: camera.quaternion.w,
            },
            target: {
              x: controls.target.x,
              y: controls.target.y,
              z: controls.target.z,
            },
            fov: camera.fov,
          }
        : kf
    )

    setCameraKeyframes(updated)
    cameraKeyframesRef.current = updated

    setFlashKeyframe(keyframeId)
    setTimeout(() => {
      setFlashKeyframe(null)
    }, 600)

    const keyframeIndex = cameraKeyframes.findIndex((kf) => kf.id === keyframeId) + 1
    showCameraAnimToast(`Keyframe #${keyframeIndex} updated`, 'update')
  }, [cameraKeyframes, showCameraAnimToast, cameraRef, controlsRef])

  // Delete keyframe
  const deleteCameraKeyframe = useCallback((keyframeId) => {
    const keyframeIndex = cameraKeyframes.findIndex((kf) => kf.id === keyframeId) + 1

    setFlashKeyframe(keyframeId)

    setTimeout(() => {
      const updated = cameraKeyframes.filter((kf) => kf.id !== keyframeId)
      setCameraKeyframes(updated)
      cameraKeyframesRef.current = updated
      setSelectedCameraKeyframe(null)
      setFlashKeyframe(null)

      showCameraAnimToast(`Keyframe #${keyframeIndex} deleted`, 'delete')
    }, 300)
  }, [cameraKeyframes, showCameraAnimToast])

  // Clear all keyframes
  const clearCameraKeyframes = useCallback(() => {
    const count = cameraKeyframes.length
    setCameraKeyframes([])
    cameraKeyframesRef.current = []
    setSelectedCameraKeyframe(null)
    showCameraAnimToast(`${count} keyframes cleared`, 'delete')
  }, [cameraKeyframes, showCameraAnimToast])

  // Interpolate at time
  const interpolateCamera = useCallback((currentTime) => {
    if (!window.THREE_MODULE) return
    
    // Use window globals (more reliable)
    const camera = window.MAIN_CAMERA || cameraRef?.current
    const controls = window.ORBIT_CONTROLS || controlsRef?.current
    
    if (!camera || !controls) return
    
    interpolateCameraAtTime(
      cameraKeyframesRef.current,
      currentTime,
      cameraInterpolationType,
      camera,
      controls,
      window.THREE_MODULE
    )
  }, [cameraInterpolationType, cameraRef, controlsRef])

  // Export keyframes
  const exportKeyframes = useCallback(() => {
    const data = {
      keyframes: cameraKeyframes,
      interpolationType: cameraInterpolationType,
    }
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: 'application/json',
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'camera-animation.json'
    a.click()
    URL.revokeObjectURL(url)

    showCameraAnimToast(`${cameraKeyframes.length} keyframes exported`, 'success')
  }, [cameraKeyframes, cameraInterpolationType, showCameraAnimToast])

  // Import keyframes
  const importKeyframes = useCallback((file) => {
    const reader = new FileReader()
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target.result)
        if (data.keyframes && Array.isArray(data.keyframes)) {
          setCameraKeyframes(data.keyframes)
          cameraKeyframesRef.current = data.keyframes
          if (data.interpolationType) {
            setCameraInterpolationType(data.interpolationType)
          }
          showCameraAnimToast(
            `${data.keyframes.length} keyframes imported successfully`,
            'success'
          )
        }
      } catch (err) {
        console.error('Error importing:', err)
        showCameraAnimToast('Failed to import animation', 'delete')
      }
    }
    reader.readAsText(file)
  }, [showCameraAnimToast])

  return {
    cameraKeyframes,
    setCameraKeyframes,
    cameraKeyframesRef,
    selectedCameraKeyframe,
    setSelectedCameraKeyframe,
    isRecordingCameraAnimation,
    setIsRecordingCameraAnimation,
    cameraInterpolationType,
    setCameraInterpolationType,
    cameraAnimationDuration,
    setCameraAnimationDuration,
    flashKeyframe,
    setFlashKeyframe,
    cameraAnimToast,
    setCameraAnimToast,
    showCameraAnimToast,
    
    // Methods
    addCameraKeyframe,
    updateCameraKeyframe,
    deleteCameraKeyframe,
    clearCameraKeyframes,
    interpolateCamera,
    exportKeyframes,
    importKeyframes,
  }
}

